/**
 * File: BuildTable.js
 * Origin: isotect
 * Description: [TODO: Description]
 *
 * Version: v1.0.1
 * Created: 2025-07-23
 * Last Updated: 2026-01-09
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

/**
 * Renders an HTML table from an array of objects with optional caption
 * and transpose functionality.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} props.object - Array of objects used as table data. Each object must have the same set of keys.
 * @param {string|null} [props.caption] - Optional table caption displayed above the table.
 * @param {boolean} [props.transpose] - Whether to transpose rows and columns.
 * @param {Array<string>|null} [props.transposeHeader] - Optional header row used when the table is transposed.
 *
 * @returns {JSX.Element} A rendered table element or an error message if data is invalid.
 */
function BuildTable({
    object,
    caption = null,
    transpose = false,
    transposeHeader = null,
}) {
    if (!Array.isArray(object) || object.length === 0) {
        return <p>Error: Invalid or empty table data</p>;
    }

    const headers = Object.keys(object[0]);
    const isValid = object.every(
        (row) => Object.keys(row).length === headers.length
    );

    if (!isValid) {
        return <p>Error: Number of columns do not match</p>;
    }

    let rows = object.map((row) => Object.values(row));

    if (transpose) {
        rows = transposeTable(headers, rows);
    }

    return (
        <>
            <table>
                {caption !== null ? (
                    <caption>
                        {" "}
                        <h4>{caption}</h4>{" "}
                    </caption>
                ) : null}
                {!transpose ? (
                    <thead>
                        <TableRow values={headers} isHeader />
                    </thead>
                ) : null}
                {transpose && transposeHeader !== null ? (
                    <thead>
                        <TableRow values={transposeHeader} isHeader />
                    </thead>
                ) : null}
                <tbody>
                    {rows.map((row, index) => (
                        <TableRow
                            key={index}
                            values={row}
                            isTransposed={transpose}
                        />
                    ))}
                </tbody>
            </table>
        </>
    );
}

/**
 * [TODO: Component description here]
 *
 * @param {Object} props - Component properties
 * @returns {JSX.Element} [TODO: Rendered output]
 */
function TableRow({ values, isTransposed, isHeader = false }) {
    return (
        <tr>
            {values.map((value, index) =>
                isTransposed && index === 0 ? (
                    <th key={index} className="table-row--transposed">
                        {value}
                    </th>
                ) : isHeader ? (
                    <th key={index}>{value}</th>
                ) : (
                    <td key={index}>{value}</td>
                )
            )}
        </tr>
    );
}

/**
 * [TODO: Component description here]
 *
 * @param {Object} props - Component properties
 * @returns {JSX.Element} [TODO: Rendered output]
 */
function transposeTable(headers, rows) {
    const numColumns = rows.length + 1;
    const numRows = headers.length;

    const originalMatrix = [headers];
    let transposeMatrix = [];

    for (let i = 0; i < rows.length; i++) {
        originalMatrix.push(rows[i]);
    }

    for (let i = 0; i < numRows; i++) {
        const row = [];
        for (let j = 0; j < numColumns; j++) {
            row.push("");
        }
        transposeMatrix.push(row);
    }

    for (let i = 0; i < originalMatrix.length; i++) {
        let row = originalMatrix[i];
        for (let j = 0; j < row.length; j++) {
            transposeMatrix[j][i] = originalMatrix[i][j];
        }
    }

    return transposeMatrix;
}

export default BuildTable;
